import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
	AbstractCRUDService,
	AlarisApiService,
	AlarisConfigService,
} from '@campaign-portal/components-library';
import { ReadRequest, ReadResponse } from '@campaign-portal/namespace/common/implementations';
import { Filter, RPCRequestParams } from '@campaign-portal/namespace/common/rpc.params';
import { CamaraRulesFieldsService } from './camara-rules.fields.service';
import { ExportService } from '@helpers/api/export.service';
import { CamaraRulesReadRequest } from '@helpers/types/hlr';
import { CamaraRule, RelatedEntity } from '@helpers/types/models';
import {
	CamaraRulesUpdateRequest,
	CamaraRulesCreateResponse,
	CamaraRulesUpdateResponse,
	CamaraRulesDeleteRequest,
	CamaraRulesDeleteResponse,
} from '@helpers/types/hlr';

@Injectable({ providedIn: 'root' })
export class CamaraRulesService implements AbstractCRUDService {
	readonly entity = '';
	readonly title = '';
	readonly loading$ = new BehaviorSubject<boolean>(false);
	readonly create = this.update;

	constructor(
		private readonly api: AlarisApiService,
		private readonly config: AlarisConfigService,
		private readonly fields: CamaraRulesFieldsService,
		private readonly exportService: ExportService,
	) {}

	read(params: ReadRequest): Observable<ReadResponse<CamaraRule[]>> {
		const parsedParams = this.prepareParams(params);
		return this.api
			.loader<{ data: CamaraRule[]; length: number }>('CamaraRules.Read', parsedParams, this.loading$)
			.pipe(
				map((resp) => {
					const data = (resp.data || []).map((item: any) => {
						if (item.client_ids !== undefined && item.client_ids !== null) {
							item.clientList = item.client_ids;
							delete item.client_ids;
						}
						if (item.author) {
							const firstName = item.author.details?.firstName || '';
							const lastName = item.author.details?.lastName || '';
							item.createdBy = firstName && lastName
								? `${firstName} ${lastName}` : item.author.login || '';
						} else {
							item.createdBy = '';
						}
						return item;
					});
					return {
						Success: true,
						Total: resp.length,
						Data: data,
					};
				})
			);
	}

	update(params: CamaraRulesUpdateRequest): Observable<CamaraRulesUpdateResponse | CamaraRulesCreateResponse> {
		const method = params.id ? 'CamaraRules.Update' : 'CamaraRules.Create';
		return this.api.loader<CamaraRulesUpdateResponse | CamaraRulesCreateResponse>(method, params, this.loading$);
	}

	delete(params: CamaraRulesDeleteRequest): Observable<CamaraRulesDeleteResponse> {
		return this.api.loader<CamaraRulesDeleteResponse>('CamaraRules.Delete', params, this.loading$);
	}

	export(total: number, params: RPCRequestParams): Observable<void> {
		params = { ...params, Paging: { Skip: 0, Take: total } };
		return this.exportService.export(
			this.config.api,
			'CamaraRules.Read',
			this.prepareParams(params),
			this.fields.headers,
			'camara_rules'
		);
	}

	private prepareParams(params: RPCRequestParams): CamaraRulesReadRequest {
		const filters: { key: string; value: string }[] = [];
		if (params.Filters) {
			for (const f of params.Filters) {
				const parsed = this.parseFilterField(f);
				if (Array.isArray(parsed)) {
					filters.push(...parsed);
				} else if (parsed.key && parsed.value) {
					filters.push(parsed);
				}
			}
		}
		return {
			offset: params.Paging?.Skip ?? 0,
			limit: params.Paging?.Take ?? 25,
			active: 'id',
			direction: 'asc',
			filter: filters,
		};
	}

	private parseFilterField(filter: Filter): { key: string; value: string }[] | { key: string; value: string } {
		const variable = filter.Field;
		const value = filter.Value;

		const normalizeArray = (val: unknown): any[] => {
			return Array.isArray(val) ? val : [val];
		};

		const extractValues = (arr: any[]): any[] => {
			return arr.map(item => {
				if (typeof item === 'object' && item !== null) {
					if ('id' in item) {
						return item.id;
					}
					if ('value' in item) {
						return item.value;
					}
					return String(item);
				}
				return item;
			});
		};

		switch (variable) {
			case 'id':
				return { key: 'id', value: String(value) };

			case 'ruleType':
				return { key: 'ruleType', value: extractValues(normalizeArray(value)).join(',') };

			case 'clientList':
				return { key: 'clientList', value: extractValues(normalizeArray(value)).join(',') };

			case 'prefixList':
				return { key: 'prefixList', value: extractValues(normalizeArray(value)).join(',') };

			case 'source':
				return { key: 'source', value: extractValues(normalizeArray(value)).join(',') };

			case 'createdBy':
				return { key: 'authorId', value: extractValues(normalizeArray(value)).join(',') };

			case 'lastUpdated': {
				const range = value as any;
				const result: { key: string; value: string }[] = [];
				if (range?.Start) {
					result.push({ key: 'lastUpdatedFrom', value: range.Start });
				}
				if (range?.End) {
					result.push({ key: 'lastUpdatedTo', value: range.End });
				}
				return result.length ? result : { key: 'lastUpdated', value: '' };
			}

			default:
				return { key: variable, value: Array.isArray(value) ? value.join(',') : String(value) };
		}
	}

	getRelatedClients(ruleType: string): Observable<RelatedEntity[]> {
		return this.api
			.loader<{ data: RelatedEntity[]; length: number }>('CamaraRules.GetRelatedClients', { ruleType },
				this.loading$)
			.pipe(map(resp => resp.data || []));
	}

	getRelatedSources(ruleType: string): Observable<RelatedEntity[]> {
		return this.api
			.loader<{ data: RelatedEntity[]; length: number }>('CamaraRules.GetRelatedSources', { ruleType },
				this.loading$)
			.pipe(map(resp => resp.data || []));
	}
}